import type en from "../en/ai";
import type { Messages } from "../../types";

const fr: Messages<typeof en> = {
  dockButton: "Demander à l’IA",
  dockTitle: "Assistant IA",
  openFull: "Ouvrir en plein écran",
  contextPage: "Travaille sur la page ouverte dans l’éditeur",
  editorStale: "L’assistant a modifié cette boutique. Rechargez l’éditeur pour voir le dernier brouillon avant de continuer.",
  reloadEditor: "Recharger l’éditeur",
  unavailableTitle: "L’assistant IA n’est pas disponible",
  unavailableBody: "Aucun fournisseur d’IA n’est configuré sur ce serveur. Un administrateur doit ajouter une clé OpenAI (Admin → IA et crédits → Labo des voix) ou définir ANTHROPIC_API_KEY. Le reste du tableau de bord fonctionne normalement.",
  introEdit: "Dites-moi quoi modifier, par écrit ou à la voix. Je modifie des brouillons que vous pouvez prévisualiser avant toute mise en ligne.",
  introCreate: "Décrivez la boutique souhaitée : ce que vous vendez, à qui, votre style et votre pays. Je la crée et je prépare les pages, le design et la navigation.",
  safetyNote: "Je ne modifie jamais les prix, le stock, les paiements ni les domaines, et je demande confirmation avant de publier ou de supprimer.",
  suggestHero: "Rends la bannière d’accueil plus haut de gamme et ajoute une section d’avantages en dessous",
  suggestFaq: "Ajoute une FAQ sur la livraison et les retours à la page FAQ",
  suggestColors: "Utilise une palette vert foncé et crème avec une police à empattements pour les titres",
  suggestTranslate: "Ajoute le français à ma boutique et traduis les pages",
  suggestCreate1: "Une boutique de céramiques artisanales marocaines vendant en Europe, style chaleureux et terreux, prix en EUR",
  suggestCreate2: "Une marque de streetwear minimaliste aux États-Unis, design noir et blanc affirmé",
  placeholderEdit: "ex. « Change le titre de la bannière en … »",
  placeholderCreate: "ex. : Je veux créer une boutique de vestes nommée Jaketo — uniquement des vestes pour hommes, devise MAD, basée au Maroc, site en français, style moderne noir et blanc…",
  inputLabel: "Message à l’assistant",
  send: "Envoyer",
  working: "En cours…",
  thinking: "Réflexion…",
  speak: "Parler",
  stopListening: "Arrêter l’écoute",
  listening: "J’écoute… parlez maintenant",
  spoken: "Dicté",
  speakReplies: "Lire les réponses à voix haute",
  enterHint: "Entrée pour envoyer · Maj+Entrée pour un saut de ligne",
  stepsLabel: "Ce que l’assistant a fait",
  confirmTitle: "Confirmation requise",
  savedAsDraft: "Enregistré en brouillon",
  live: "En ligne",
  previewChanges: "Prévisualiser ↗",
  undoChanges: "Annuler ces modifications",
  connectionLost: "La connexion a été interrompue avant que la demande n’atteigne le serveur. Réessayez.",
  connectionLostRunning: "La connexion a été interrompue, mais l’assistant continue sur le serveur. Réessayez pour voir le résultat : il ne s’exécutera pas deux fois.",
  voiceUnsupported: "La saisie vocale n’est pas prise en charge par ce navigateur. Utilisez une version récente de Chrome, Edge ou Safari, ou tapez votre demande.",
  voicePermissionDenied: "L’accès au micro est bloqué. Autorisez le micro pour ce site dans les réglages du navigateur, ou tapez votre demande.",
  voiceNoMic: "Aucun micro détecté. Branchez-en un ou tapez votre demande.",
  voiceNoSpeech: "Je n’ai rien entendu. Touchez le micro et réessayez.",
  voiceNetwork: "Le service vocal du navigateur est injoignable. Vérifiez votre connexion ou tapez votre demande.",
  voiceAborted: "Écoute arrêtée.",
  voiceFailed: "La saisie vocale a échoué. Veuillez taper votre demande.",
  tool_get_store_overview: "Lecture de la boutique",
  tool_get_page: "Lecture de la page",
  tool_list_section_types: "Vérification des sections disponibles",
  tool_list_media: "Consultation de vos images",
  tool_list_products: "Consultation de vos produits",
  tool_list_collections: "Consultation de vos collections",
  tool_edit_page_sections: "Modification des sections",
  tool_create_page: "Création d’une page",
  tool_update_page_details: "Mise à jour des détails de la page",
  tool_publish_page: "Publication de la page",
  tool_delete_page: "Suppression d’une page",
  tool_set_theme: "Changement de thème",
  tool_update_theme_settings: "Mise à jour des couleurs et polices",
  tool_publish_theme: "Publication du thème",
  tool_update_menu: "Mise à jour de la navigation",
  tool_update_store_details: "Mise à jour de la description",
  tool_update_product_text: "Réécriture du texte produit",
  tool_create_collection: "Création d’une collection",
  tool_enable_storefront_language: "Ajout d’une langue à la boutique",
  tool_get_translation_status: "Vérification des traductions",
  tool_translate_content: "Traduction du contenu",
  tool_publish_storefront_language: "Publication d’une langue",
  tool_publish_store: "Publication de la boutique",
  tool_set_dashboard_language: "Changement de la langue du tableau de bord",
  tool_create_store: "Création de votre boutique",
  pageTitle: "Assistant IA",
  pageDescription: "Créez et modifiez votre boutique en écrivant ou en parlant. Le texte et la voix utilisent le même assistant et le même historique.",
  historyTitle: "Historique des modifications",
  historyDescription: "Tout ce que l’assistant a fait pour vous dans cette boutique, du plus récent au plus ancien.",
  historyEmpty: "Aucune modification par l’assistant pour l’instant.",
  status_running: "En cours",
  status_awaiting_confirmation: "En attente de confirmation",
  status_succeeded: "Terminé",
  status_partial: "Partiellement terminé",
  status_failed: "Échec",
  status_cancelled: "Annulé",
  outcome_draft: "Brouillon",
  outcome_published: "En ligne",
  outcome_none: "Aucune modification",
  stepUndone: "Annulé",
  viaVoice: "à la voix",
  viaText: "par écrit",
  undo: "Annuler",
  undoing: "Annulation…",
  createTitle: "Créer une boutique avec l’IA",
  createDescription: "Décrivez votre activité et l’assistant construit une première version de votre boutique : thème, couleurs, page d’accueil, pages et navigation. Rien n’est publié sans votre accord.",
  createManual: "Vous préférez un formulaire ? Créer une boutique manuellement",
  storeReady: "Votre boutique est prête à être vérifiée.",
  openStore: "Ouvrir le tableau de bord de la boutique",
  levelLabel: "Qualité du modèle",
  level_low: "Faible",
  level_medium: "Moyenne",
  level_high: "Élevée",
  level_max: "Max",
  levelTooltip: "{level} ≈ ×{multiplier}",
  levelLocked: "{level} n'est pas disponible avec votre forfait",
  levelLowHint: "Faible est déconseillé pour les constructions complexes. Ajoutez des crédits pour utiliser un niveau supérieur.",
  creditsLeft: "Il reste {count} crédits — rechargez pour continuer à construire",
  outOfCredits: "Vous n'avez plus de crédits — alimentez votre portefeuille pour continuer",
  fundWallet: "Alimenter le portefeuille",
  addCredits: "Ajouter des crédits",
  upgradePlan: "Changer de forfait",
  chargeCredits: "−{credits} ⚡",
  chargeFree: "gratuit (inclus dans le forfait)",
  chargeTitle: "Coût de cette réponse",
  planButton: "Planifier",
  planHint: "Obtenir d'abord un plan étape par étape — rien n'est modifié",
  planLabel: "Plan",
  buildIt: "Construire",
  buildItText: "Construis-le comme prévu.",
  attach: "Joindre une image",
  attachNeedsStore: "Vous pourrez joindre des images une fois la boutique créée",
  uploading: "Envoi…",
  uploadFailed: "L'image n'a pas pu être envoyée.",
  removeAttachment: "Retirer l'image",
  attachedImage: "Image jointe",
  stepsCount: "Étapes {done}/{total}",
  queued: "En attente : {text}",
  cancelQueued: "Retirer le message en attente",
  queuePlaceholder: "Préparez votre prochain message…",
  stop: "Arrêter",
  phase_store: "Création de votre boutique",
  phase_images: "Création des images",
  phase_pages: "Rédaction des pages",
  phase_styling: "Application du style de la marque",
  phase_navigation: "Création de la navigation",
  phase_translating: "Traduction",
  phase_details: "Mise à jour des informations",
  phase_publishing: "Publication",
  phase_reading: "Analyse de votre boutique",
  phase_thinking: "Préparation de l’étape suivante",
  phase_planning: "Planification",
  phase_working: "En cours",
  phase_done: "Terminé",
  progressLog: "Ce que fait l’assistant",
  progressLabel: "Progression",
  milestonesLabel: "Liste des étapes",
  chooseToContinue: "Choisissez une réponse pour continuer",
  generating: "Génération…",
  ready: "Prêt",
  preparingWorkspace: "Préparation de votre espace",
  previewWaiting: "Votre boutique s’affiche ici dès que l’assistant la crée.",
  livePreview: "Aperçu en direct",
  openStudio: "Ouvrir dans AI Studio",
  stepsPill: "Étapes {done}/{total}",
  callBuilding: "Création de votre boutique…",
  callWatchLive: "Voir en direct",
  backToDashboard: "Retour au tableau de bord",
  storeReadyTitle: "Votre boutique est prête !",
  storeReadyBody: "Vous pouvez la gérer depuis votre tableau de bord : produits, commandes, design, paiements et publication.",
  storeReadyDashboard: "Ouvrir le tableau de bord",
  storeReadyDesign: "Continuer le design",
  limitPlanTitle: "La limite de votre plan est atteinte",
  limitPlanBody: "Votre plan actuel ne permet pas cela (par exemple une autre boutique). Passez à un plan supérieur pour continuer — rien n’a été facturé.",
  limitCreditsTitle: "Vous n’avez plus de crédits IA",
  limitCreditsBody: "Achetez des crédits ou passez à un plan supérieur pour continuer.",
  limitLevelTitle: "Ce niveau de modèle n’est pas inclus dans votre plan",
  limitLevelBody: "Choisissez un niveau inférieur ou passez à un plan supérieur pour des modèles plus puissants.",
  buyCredits: "Acheter des crédits",
  myStores: "Mes boutiques",
};

export default fr;
